import { InlineCodeHighlight } from '@mantine/code-highlight';
import { match } from 'ts-pattern';
import type { EmailExtractionType } from '~/common/stably-email/stably-email-types';
import type { ReceiveEmailPart } from '../converters';

const extractionTypeToLabel = (extractionType: EmailExtractionType) =>
  match(extractionType)
    .with(undefined, () => 'None (Wait for the email)')
    .with('otp', () => 'OTP (One-Time Password)')
    .with('magic-link', () => 'Magic Link')
    // this is unused, since we just display the prompt in this case.
    .with('custom-extraction', () => 'Content from email body')
    .exhaustive();

/**
 * Read-only summary shown in the step row (the `Popover.Target`). Renders either
 * the configured extraction, or the "wait for email" description with any filters.
 */
export function ReceiveEmailSummary({
  variableExtraction,
  toEmailAddressValue,
  filters,
}: Pick<ReceiveEmailPart, 'variableExtraction' | 'toEmailAddressValue' | 'filters'>) {
  if (variableExtraction?.extractionType) {
    return (
      <>
        Store extracted{' '}
        {variableExtraction.extractionType === 'custom-extraction' ?
          <>
            <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">
              &apos;{variableExtraction.customExtractionPrompt}&apos;
            </span>{' '}
          </>
        : <>
            <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">
              {extractionTypeToLabel(variableExtraction.extractionType)}
            </span>{' '}
          </>
        }
        from inbox{' '}
        <InlineCodeHighlight
          code={toEmailAddressValue || '???'}
          language="tsx"
        />{' '}
        as variable{' '}
        <InlineCodeHighlight
          code={variableExtraction?.variableName || '???'}
          language="tsx"
        />{' '}
      </>
    );
  }

  return (
    <>
      Wait for email{' '}
      {!!filters?.sender && (
        <>
          from{' '}
          <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">{filters.sender}</span>{' '}
        </>
      )}
      {!!filters?.subject && (
        <>
          with subject{' '}
          <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">{filters.subject}</span>{' '}
        </>
      )}
      in inbox{' '}
      <InlineCodeHighlight
        code={toEmailAddressValue || '???'}
        language="tsx"
      />{' '}
    </>
  );
}
